import { useEffect, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { format, setMilliseconds } from 'date-fns';

import { timeFormat } from '@sternum/web/config';
import { getTimeLeft, useCountDown } from '@sternum/web/hooks/useCountDown';
import { namespaces } from '@sternum/web/services/i18n';

import { TimerCountdown, TimerText } from './MFACodeTimer.styles';

const SECOND = 1000;

export interface MFACodeTimerProps {
  expiresAt: Date;
  onExpired?: () => unknown;
}

export function MFACodeTimer({ expiresAt, onExpired }: MFACodeTimerProps) {
  const { t } = useTranslation(namespaces.auth);

  const expiration = useMemo(
    () => ({
      from: setMilliseconds(expiresAt, 0).getTime(),
      to: setMilliseconds(new Date(), 0).getTime(),
    }),
    [expiresAt],
  );

  const counter = useCountDown({ from: expiration.from, to: expiration.to, amountToSubtract: SECOND });
  const timeLeft = getTimeLeft(counter - expiration.to);

  useEffect(() => {
    if (counter <= expiration.to) onExpired?.();
  }, [counter, onExpired, expiration.to]);

  const formattedTime = format(timeLeft, timeFormat.timeLong);

  return (
    <TimerText>
      <Trans
        t={t}
        i18nKey="mfaCodeTimer"
        values={{ timeLeft: formattedTime }}
        components={{ strong: <TimerCountdown as="span" /> }}
        role="presentation"
        aria-label="time left for resend"
      />
    </TimerText>
  );
}
